import React, { createContext, useContext, useState, useEffect } from 'react';
import { storeConfig } from '../data/config';

// Create the context
const CartContext = createContext();
const LEGACY_CART_KEY = 'cart';
const SCOPED_CART_KEY = storeConfig.storeKey ? `cart:${storeConfig.storeKey}` : LEGACY_CART_KEY;

function sanitizeCartItems(items) {
    return Array.isArray(items) ? items.map(item => ({
        ...item,
        qty: Number(item.qty) || 1,
        price: Number(item.price) || 0
    })) : [];
}

function readStoredCart(storageKey) {
    const storedCart = localStorage.getItem(storageKey);
    if (storedCart === null) {
        return null;
    }

    try {
        return sanitizeCartItems(JSON.parse(storedCart));
    } catch (e) {
        console.error(`Failed to parse cart for key "${storageKey}"`, e);
        localStorage.removeItem(storageKey);
        return null;
    }
}

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [isExploding, setIsExploding] = useState(false);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [isCartHydrated, setIsCartHydrated] = useState(false);

    // Load cart from localStorage on mount
    useEffect(() => {
        const scopedCart = readStoredCart(SCOPED_CART_KEY);

        if (scopedCart && scopedCart.length > 0) {
            setCartItems(scopedCart);
            setIsCartHydrated(true);
            return;
        }

        const legacyCart = readStoredCart(LEGACY_CART_KEY);
        if (legacyCart && legacyCart.length > 0) {
            setCartItems(legacyCart);
            localStorage.setItem(SCOPED_CART_KEY, JSON.stringify(legacyCart));

            if (SCOPED_CART_KEY !== LEGACY_CART_KEY) {
                localStorage.removeItem(LEGACY_CART_KEY);
            }
        } else if (scopedCart) {
            setCartItems(scopedCart);
        }

        setIsCartHydrated(true);
    }, []);

    // Save cart to localStorage whenever it changes
    useEffect(() => {
        if (!isCartHydrated) {
            return;
        }

        localStorage.setItem(SCOPED_CART_KEY, JSON.stringify(cartItems));
    }, [cartItems, isCartHydrated]);

    const addItem = (item) => {
        setCartItems((prev) => {
            const quantity = Number(item.qty) || 1;
            const existingItemIndex = prev.findIndex((i) => i.id === item.id);
            if (existingItemIndex > -1) {
                const newCart = [...prev];
                newCart[existingItemIndex] = {
                    ...newCart[existingItemIndex],
                    qty: (Number(newCart[existingItemIndex].qty) || 0) + quantity
                };
                return newCart;
            }
            return [...prev, { ...item, qty: quantity, price: Number(item.price) || 0 }];
        });
        setIsExploding(true);
        setTimeout(() => setIsExploding(false), 2000);
    };

    const removeItem = (id) => {
        setCartItems((prev) => prev.filter((item) => item.id !== id));
    };

    const updateItemQuantity = (id, newQty) => {
        setCartItems((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, qty: parseInt(newQty, 10) } : item
            )
        );
    };

    const clearCart = () => setCartItems([]);

    const cartCount = cartItems.reduce((acc, item) => acc + (Number(item.qty) || 0), 0);

    const cartTotal = cartItems.reduce((acc, item) => acc + ((Number(item.price) || 0) * (Number(item.qty) || 0)), 0);

    const addToCart = (item) => {
        addItem(item);
    };

    return (
        <CartContext.Provider value={{
            cartItems,
            addItem,
            removeItem,
            updateItemQuantity,
            clearCart,
            cartCount,
            cartTotal,
            isExploding,
            isCartOpen,
            setIsCartOpen,
            addToCart
        }}>
            {children}
        </CartContext.Provider>
    );
};
